import type { Edge, Node } from "@xyflow/react";
import type { NodeData } from "~/lib/types";
import { getDefinition } from "~/nodes/registry";

/** An xyflow node whose `type` is always one of the registered definitions. */
export type SpiroNode = Node<NodeData> & { type: string };

export interface SerializedGraph {
  version: 2;
  nodes: {
    id: string;
    type: string;
    position: { x: number; y: number };
    data: NodeData;
  }[];
  edges: {
    id: string;
    source: string;
    sourceHandle: string;
    target: string;
    targetHandle: string;
  }[];
  isPreset?: boolean;
}

let idCounter = 1;

export function createNodeId(): string {
  return `n${idCounter++}`;
}

/** Keeps generated ids from colliding with the ids in a graph we just loaded. */
function reserveIds(ids: string[]): void {
  for (const id of ids) {
    const numeric = Number.parseInt(id.replace(/^n/, ""), 10);
    if (Number.isFinite(numeric) && numeric >= idCounter)
      idCounter = numeric + 1;
  }
}

export function edgeId(
  source: string,
  sourceHandle: string,
  target: string,
  targetHandle: string,
): string {
  return `${source}:${sourceHandle}->${target}:${targetHandle}`;
}

/**
 * Drops keys a node definition doesn't know about. Saved rete graphs carry
 * transient junk in `data` — live `setInterval` handles, computed preview
 * readouts — none of which should survive a round trip.
 */
function cleanData(type: string, data: unknown): NodeData {
  const definition = getDefinition(type);
  if (!definition || typeof data !== "object" || data === null)
    return { ...definition?.defaults };

  const source = data as NodeData;
  const cleaned: NodeData = { ...definition.defaults };

  for (const key of Object.keys(definition.defaults)) {
    if (source[key] !== undefined) cleaned[key] = source[key];
  }

  return cleaned;
}

export function serializeGraph(
  nodes: SpiroNode[],
  edges: Edge[],
): SerializedGraph {
  return {
    version: 2,
    nodes: nodes.map((node) => ({
      id: node.id,
      type: node.type,
      position: { x: node.position.x, y: node.position.y },
      data: cleanData(node.type, node.data),
    })),
    edges: edges.flatMap((edge) =>
      edge.sourceHandle && edge.targetHandle
        ? [
            {
              id: edge.id,
              source: edge.source,
              sourceHandle: edge.sourceHandle,
              target: edge.target,
              targetHandle: edge.targetHandle,
            },
          ]
        : [],
    ),
  };
}

/** Node `name` in rete v1 saves → the `type` of the equivalent definition here. */
const RETE_V1_TYPES: Record<string, string> = {
  Number: "number",
  Add: "add",
  Math: "math",
  Custom: "custom",
  Sphere: "sphere",
  Cube: "cube",
  "Add Functions": "addFunctions",
  "Multiply Functions": "multiplyFunctions",
  Animate: "animate",
  Audio: "audio",
  "Solid Color": "solidColor",
  "Index Color": "indexColor",
  "Color Picker": "colorPicker",
  "Extrusion Style": "extrusionStyle",
  "Shader Style": "shaderStyle",
  "Point Style": "pointsStyle",
  Transform: "transform",
  Output: "output",
};

interface ReteV1Connection {
  node: number;
  output?: string;
  input?: string;
}

interface ReteV1Node {
  name?: string;
  position?: [number, number];
  data?: NodeData;
  inputs?: Record<string, { connections?: ReteV1Connection[] }>;
  outputs?: Record<string, { connections?: ReteV1Connection[] }>;
}

interface ReteV1Graph {
  nodes: Record<string, ReteV1Node>;
}

function isReteV1Graph(value: unknown): value is ReteV1Graph {
  if (typeof value !== "object" || value === null) return false;
  const nodes = (value as { nodes?: unknown }).nodes;
  return typeof nodes === "object" && nodes !== null && !Array.isArray(nodes);
}

/**
 * Converts a graph saved by the old rete v1 editor — `{ nodes: { "1": {...} } }`,
 * with connections listed on each input — into the flat nodes/edges shape xyflow
 * wants. Without this the seven demo presets and every user-saved sketch break.
 */
function migrateReteV1(graph: ReteV1Graph): SerializedGraph {
  const nodes: SerializedGraph["nodes"] = [];
  const edges: SerializedGraph["edges"] = [];

  for (const [key, node] of Object.entries(graph.nodes)) {
    const type = RETE_V1_TYPES[node.name ?? ""];
    if (!type) continue;

    nodes.push({
      id: `n${key}`,
      type,
      position: { x: node.position?.[0] ?? 0, y: node.position?.[1] ?? 0 },
      data: cleanData(type, node.data),
    });
  }

  const migrated = new Set(nodes.map((n) => n.id));
  const seen = new Set<string>();

  const link = (
    source: string,
    sourceHandle: string,
    target: string,
    targetHandle: string,
  ): void => {
    if (!migrated.has(source) || !migrated.has(target)) return;

    const id = edgeId(source, sourceHandle, target, targetHandle);
    if (seen.has(id)) return;

    seen.add(id);
    edges.push({ id, source, sourceHandle, target, targetHandle });
  };

  // rete recorded every connection twice, once on each end. Read both sides so a
  // save that only kept one of them still migrates completely.
  for (const [key, node] of Object.entries(graph.nodes)) {
    const self = `n${key}`;

    for (const [inputKey, input] of Object.entries(node.inputs ?? {})) {
      for (const connection of input.connections ?? []) {
        if (connection.output)
          link(`n${connection.node}`, connection.output, self, inputKey);
      }
    }

    for (const [outputKey, output] of Object.entries(node.outputs ?? {})) {
      for (const connection of output.connections ?? []) {
        if (connection.input)
          link(self, outputKey, `n${connection.node}`, connection.input);
      }
    }
  }

  return { version: 2, nodes, edges };
}

function isSerializedGraph(value: unknown): value is SerializedGraph {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as { nodes?: unknown; edges?: unknown };
  return Array.isArray(candidate.nodes) && Array.isArray(candidate.edges);
}

/** Accepts either the current format or a legacy rete v1 save. */
export function deserializeGraph(raw: unknown): {
  nodes: SpiroNode[];
  edges: Edge[];
} {
  let graph: SerializedGraph;

  if (isSerializedGraph(raw)) graph = raw;
  else if (isReteV1Graph(raw)) graph = migrateReteV1(raw);
  else return { nodes: [], edges: [] };

  const nodes: SpiroNode[] = graph.nodes.flatMap((node) =>
    getDefinition(node.type)
      ? [
          {
            id: node.id,
            type: node.type,
            position: node.position,
            data: cleanData(node.type, node.data),
          },
        ]
      : [],
  );

  const known = new Set(nodes.map((n) => n.id));

  const edges: Edge[] = graph.edges
    .filter((edge) => known.has(edge.source) && known.has(edge.target))
    .map((edge) => ({
      id:
        edge.id ||
        edgeId(edge.source, edge.sourceHandle, edge.target, edge.targetHandle),
      source: edge.source,
      sourceHandle: edge.sourceHandle,
      target: edge.target,
      targetHandle: edge.targetHandle,
    }));

  reserveIds(nodes.map((n) => n.id));

  return { nodes, edges };
}
